import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Shirt, Layers, ExternalLink } from "lucide-react";

const DTF_OPTIONS = [
  { label: "T-Shirts", to: "/shop/dtf?category=tees", desc: "Classic DTF cotton tees" },
  { label: "Tank Tops", to: "/shop/dtf?category=tanks", desc: "Razor-back tanks — starting at $10" },
  { label: "Hoodies", to: "/shop/dtf?category=hoodies", desc: "Comfortable pullover fleece" },
  { label: "Zip Ups", to: "/shop/dtf?category=zip-ups", desc: "Full-zip fleece hoodies" },
  { label: "Gang Sheets", to: "/shop/dtf?category=gangsheets", desc: "14\" x 18\" DTF transfers, ready to press" },
];

/**
 * Compact "Down To Film ▾" dropdown menu.
 * Variant: "nav" (dark on light navbar) | "card" (larger, for Home hero card)
 */
export default function DTFMenu({ variant = "nav", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const isCard = variant === "card";

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid={`dtf-menu-toggle-${variant}`}
        className={
          isCard
            ? "btn-brutal btn-brutal-dtf w-full sm:w-auto"
            : "label-mono inline-flex items-center gap-1.5 hover:text-dtf-blue underline-mono"
        }
      >
        {isCard ? (
          <>
            <Shirt size={16} />
            Shop DTF Apparel
            <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        ) : (
          <>
            Down To Film
            <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`dtf-menu-panel-${variant}`}
          className="absolute left-0 top-full mt-2 w-[300px] bg-white border border-black z-[60]"
          style={{ boxShadow: "6px 6px 0 0 var(--dtf-blue)" }}
        >
          <div className="border-b border-black px-4 py-3 flex items-center gap-2" style={{ background: "var(--dtf-yellow-soft)" }}>
            <img
              src="/logos/down-to-film.png"
              alt="Down To Film Apparel"
              className="h-8 w-auto object-contain"
            />
            <span className="label-mono text-dtf-blue">Standard Line</span>
          </div>

          <ul className="p-2">
            {DTF_OPTIONS.map((opt) => {
              const Icon = opt.label === "Gang Sheets" ? Layers : Shirt;
              return (
                <li key={opt.label}>
                  <Link
                    to={opt.to}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 px-3 py-2.5 hover:bg-[color:var(--dtf-yellow-soft)] transition-colors group"
                    data-testid={`dtf-menu-item-${opt.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={18} className="mt-0.5 text-dtf-blue shrink-0" />
                    <div className="flex-1">
                      <p className="font-mono text-sm font-bold text-dtf-blue group-hover:underline">
                        {opt.label}
                      </p>
                      <p className="label-mono text-[color:var(--muted-text)] text-[10px] mt-0.5">
                        {opt.desc}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="border-t border-black px-4 py-3 bg-[color:var(--muted)]">
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between label-mono text-black hover:text-dtf-blue"
              data-testid="dtf-menu-item-other"
            >
              <span>Need something else? Request a quote</span>
              <ExternalLink size={14} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
